import { BadgeCheck } from 'lucide-react';
import { BoxedText } from '@/components/common/BoxedText';
import { DetailPanel } from '@/components/common/DetailPanel';
import { DetailValue } from '@/components/common/DetailValue';
import { FIELD_LABEL_CLASS } from '@/components/common/FormDialog/formDialog.constants';
import { LabelWithValue } from '@/components/common/LabelWithValue';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatDisplayDateTime } from '@/utils/date';
import type { LeaveApplicationDetail } from '../types/leaveApplication.types';
import { LeaveApplicationReviewForm } from './LeaveApplicationReviewForm';
import { LeaveApplicationStatusBadge } from './LeaveApplicationStatusBadge';

interface ReviewActionSectionProps {
  application: LeaveApplicationDetail | undefined;
  isRefreshing: boolean;
  onCommentDirtyChange: (dirty: boolean) => void;
}

interface ReviewOutcomeProps {
  application: LeaveApplicationDetail;
}

function ReviewOutcome({ application }: ReviewOutcomeProps) {
  if (application.status === 'PENDING') return null;

  const reviewerCommentLabel = application.reviewedAt ? (
    <>
      Reviewer Comment ·{' '}
      <span className="whitespace-nowrap">{formatDisplayDateTime(application.reviewedAt)}</span>
    </>
  ) : (
    'Reviewer Comment'
  );

  return (
    <LabelWithValue label={reviewerCommentLabel}>
      <BoxedText isExpandable>{application.reviewerComment}</BoxedText>
    </LabelWithValue>
  );
}

export function ReviewActionSection({
  application,
  isRefreshing,
  onCommentDirtyChange,
}: ReviewActionSectionProps) {
  return (
    <DetailPanel title="Action" icon={BadgeCheck} hasTintedHeader headerTint="dark" size="compact">
      <div className="flex items-center gap-3">
        <span className={cn(FIELD_LABEL_CLASS, 'text-sm')}>Status</span>
        <DetailValue isLoading={application === undefined}>
          {application ? <LeaveApplicationStatusBadge status={application.status} /> : null}
        </DetailValue>
      </div>
      {application === undefined ? <Skeleton className="h-52 w-full rounded-lg" /> : null}
      {application?.status === 'PENDING' ? (
        <LeaveApplicationReviewForm
          applicationId={application.id}
          requestType={application.requestType}
          disabled={isRefreshing}
          onDirtyChange={onCommentDirtyChange}
        />
      ) : null}
      {application ? <ReviewOutcome application={application} /> : null}
    </DetailPanel>
  );
}
